import { useContext } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { AxiosContext } from "contexts/AxiosContext";
import { AttachmentDetail } from "interfaces/attachment";

const STALE_TIME = 1000 * 60 * 15;

interface EventAttachmentFilesProps {
  caseEventId: number;
}

export const useEventAttachmentFilesQuery = ({
  caseEventId
}: EventAttachmentFilesProps): UseQueryResult<AttachmentDetail[], unknown> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["attachments", "events", { caseEventId }],
    queryFn: async () => {
      const { data } = await axios.get<AttachmentDetail[]>(
        `/events/${caseEventId}/attachments`
      );

      return data;
    },
    staleTime: STALE_TIME
  });
};

interface ActionAttachmentsProps {
  actionId?: number;
}

export const useActionAttachmentsQuery = ({
  actionId
}: ActionAttachmentsProps): UseQueryResult<AttachmentDetail[], unknown> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["attachments", "action", { actionId }],
    queryFn: async () => {
      const { data } = await axios.get<AttachmentDetail[]>(
        `/case_actions/${actionId}/attachments`
      );

      return data;
    },
    enabled: !!actionId,
    staleTime: STALE_TIME
  });
};

interface ActionSubmittedAttachmentsProps {
  actionId?: number;
}

export const useActionSubmittedAttachmentsQuery = ({
  actionId
}: ActionSubmittedAttachmentsProps): UseQueryResult<
  AttachmentDetail[],
  unknown
> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["submitted_attachments", "action", { actionId }],
    queryFn: async () => {
      const { data } = await axios.get<AttachmentDetail[]>(
        `/case_actions/${actionId}/submitted_attachments`
      );

      return data;
    },
    enabled: !!actionId,
    staleTime: STALE_TIME
  });
};

interface AttachmentDetailProps {
  attachmentId: number;
}

export const useAttachmentDetailQuery = ({
  attachmentId
}: AttachmentDetailProps): UseQueryResult<AttachmentDetail, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["attachment", { attachmentId }],
    queryFn: async () => {
      const { data } = await axios.get<AttachmentDetail[]>(
        `/attachments/${attachmentId}`
      );

      return data;
    },
    enabled: !!attachmentId,
    staleTime: STALE_TIME
  });
};
